import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { useLocation } from 'react-router';
import { Workflow } from '@sierralabs/workflow';

import api, { ApiParams, FetchError, FetchMethod } from '../../common/api/api';
import AppSnackbar from '../../common/AppSnackbar';
import { parseDateFields } from '../../common/api/Api.hooks';
import { TaskType } from '../task-types/TaskType.interface';
import { Task } from '../tasks/Task.interface';
import { WorkflowDto, WorkflowVersion } from './Workflow.interface';

// can be Task, taskTypeName, or workflowId
export type GetWorkflowParamType =
  | Pick<Task, 'workflowId' | 'taskTypeName'>
  | string
  | number
  | undefined;

export function getTaskTypeOrIdValue(
  param: GetWorkflowParamType
): string | number | undefined {
  if (typeof param === 'object') {
    const { workflowId, taskTypeName } = param || {};
    return taskTypeName ? taskTypeName : workflowId ? workflowId : undefined;
  }
  return param;
}

// use api.fetch instead of hook to avoid console errors related to
// updating parent component (WorkflowProvider) while rendering child components
async function fetchWorkflowVersion(
  taskTypeOrId: string | number,
  params?: ApiParams
): Promise<WorkflowVersion | undefined> {
  try {
    const url: string =
      typeof taskTypeOrId === 'number'
        ? `workflows/version/${taskTypeOrId}`
        : `workflows/version/task-type/${taskTypeOrId}`;
    const { response } = api.fetch(FetchMethod.Get, url, params);
    const result: Response = await response;
    if (result.ok) {
      const json: any = await result.json();
      return parseDateFields(json) as WorkflowVersion | undefined;
    }
  } catch {}
}

export interface WorkflowWrapper {
  id: number;
  taskType: Pick<TaskType, 'name' | 'targetEntity'> | undefined;
  /** Task Type name value. */
  taskTypeName: WorkflowDto['taskTypeName'];
  workflow: Workflow;
}

export interface WorkflowContextState {
  workflows: WorkflowWrapper[];
  error?: FetchError;
  setError: (error: FetchError | undefined) => void;
  getWorkflow: (
    param: GetWorkflowParamType,
    params?: ApiParams
  ) => Workflow | undefined;
  loadWorkflow: (taskTypeOrId: string | number) => void;
}

const WorkflowContext = createContext<WorkflowContextState>({
  workflows: [],
  setError: () => {},
  getWorkflow: () => undefined,
  loadWorkflow: () => {}
});

export function useWorkflowContext(): WorkflowContextState {
  const context: WorkflowContextState = useContext(WorkflowContext);
  if (!context) {
    throw new Error(
      'useWorkflowContext must be used within the WorkflowProvider component.'
    );
  }
  return context;
}

const WorkflowProvider: React.FC = ({ children }) => {
  const { key } = useLocation();
  const errorRef = useRef<FetchError | undefined>();
  const workflowsRef = useRef<WorkflowWrapper[]>([]);
  // save currently fetching to avoid multiple and
  // simultaneous fetches for the same workflowVersion
  const isFetchingRef = useRef<Array<string | number>>([]);
  const [error, setErrorState] = useState<FetchError | undefined>();
  const [workflows, setWorkflows] = useState<WorkflowWrapper[]>([]);

  const addWorkflow = useCallback((workflowVersion: WorkflowVersion) => {
    const { taskType, taskTypeName } = workflowVersion.workflow || {};
    // check wrapper for existing taskType
    const index: number = workflowsRef.current.findIndex(
      (workflow: WorkflowWrapper) => {
        // check with workflowId if no taskType
        return taskTypeName
          ? workflow.taskTypeName === taskTypeName
          : workflow.id === workflowVersion.workflowId;
      }
    );
    const newWorkflow: WorkflowWrapper = {
      taskType,
      taskTypeName,
      id: workflowVersion.workflowId,
      workflow: new Workflow(workflowVersion.config)
    };

    if (index > -1) {
      workflowsRef.current.splice(index, 1, newWorkflow);
    } else {
      workflowsRef.current.push(newWorkflow);
    }
    // update workflows
    setWorkflows([...workflowsRef.current]);
  }, []);

  const findWorkflowVersion = useCallback(
    (taskTypeOrId: string | number, params?: ApiParams) => {
      // skip if currently fetching
      if (isFetchingRef.current.includes(taskTypeOrId)) {
        return;
      }

      const fetch = async () => {
        const workflowVersion: WorkflowVersion | undefined =
          await fetchWorkflowVersion(taskTypeOrId, params);

        workflowVersion && addWorkflow(workflowVersion);
        // remove from isFetching
        const index: number = isFetchingRef.current.indexOf(taskTypeOrId);
        if (index > -1) {
          isFetchingRef.current.splice(index, 1);
        }
      };

      isFetchingRef.current.push(taskTypeOrId);
      fetch();
    },
    [addWorkflow]
  );

  const getWorkflow = useCallback(
    (param: GetWorkflowParamType, params?: ApiParams): Workflow | undefined => {
      const taskTypeOrId: string | number | undefined =
        getTaskTypeOrIdValue(param);
      if (!taskTypeOrId) {
        return undefined;
      }

      const workflow: WorkflowWrapper | undefined = workflowsRef.current.find(
        (workflow: WorkflowWrapper) => {
          return typeof taskTypeOrId === 'number'
            ? workflow.id === taskTypeOrId
            : workflow.taskTypeName === taskTypeOrId;
        }
      );

      if (workflow) {
        return workflow.workflow;
      }
      // fetch workflow version if it does not exist yet
      findWorkflowVersion(taskTypeOrId, params);
    },
    // trigger hooks using this callback when workflows count changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [findWorkflowVersion, workflowsRef.current.length]
  );

  const loadWorkflow = useCallback(
    (taskTypeOrId: string | number) => {
      if (taskTypeOrId) {
        findWorkflowVersion(taskTypeOrId);
      }
    },
    [findWorkflowVersion]
  );

  const setError = useCallback((newError: FetchError | undefined) => {
    if (newError?.message !== errorRef.current?.message) {
      errorRef.current = newError;
      setErrorState(newError);
    }
  }, []);

  // clear error when changing pages
  useEffect(() => {
    errorRef.current = undefined;
    setErrorState(undefined);
  }, [key]);

  return (
    <WorkflowContext.Provider
      value={{ error, setError, workflows, getWorkflow, loadWorkflow }}
    >
      {children}
      <AppSnackbar severity='warning' open={!!error}>
        {error?.message}
      </AppSnackbar>
    </WorkflowContext.Provider>
  );
};

export default WorkflowProvider;
